/**
 * StaleRecordsPanel — test rows should never sit in a live directory.
 *
 * Lists everything the purge would remove (test-named or already deleted) and
 * removes it on approval. Purged rows are archived server-side first.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Eraser, CheckCircle2 } from 'lucide-react';

export const StaleRecordsPanel = () => {
  const qc = useQueryClient();
  const [expanded, setExpanded] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['church-stale-audit'],
    queryFn: () => api.get('/admin/church-stale-audit').then((r) => r.data),
  });

  const purge = useMutation({
    mutationFn: () => api.post('/admin/church-stale-audit/purge'),
    onSuccess: (r) => {
      toast.success(`Removed ${r.data.purged} record${r.data.purged === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['church-stale-audit'] });
      qc.invalidateQueries({ queryKey: ['admin-churches'] });
      qc.invalidateQueries({ queryKey: ['church-locations'] });
      qc.invalidateQueries({ queryKey: ['church-directory-audit'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) {
    return (
      <Card data-testid="stale-records-panel">
        <CardContent className="py-10 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const candidates = data?.candidates || [];
  const shown = expanded ? candidates : candidates.slice(0, 12);

  return (
    <Card data-testid="stale-records-panel">
      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Eraser className="h-4 w-4 text-rose-600" />
            Test record purge
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Rows left behind by seed scripts and automated tests (pytest, ORGTEST, ZZTMP)
            plus anything already deleted. Purged rows are archived first, so this is reversible.
          </p>
        </div>
        <Button size="sm" variant="destructive"
                onClick={() => {
                  if (window.confirm(`Remove ${candidates.length} test records from the directory?`)) {
                    purge.mutate();
                  }
                }}
                disabled={purge.isPending || candidates.length === 0}
                data-testid="purge-stale-records">
          {purge.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          {candidates.length ? `Purge ${candidates.length} records` : 'Directory is clean'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {candidates.length === 0 ? (
          <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="stale-records-clean">
            <CheckCircle2 className="h-4 w-4" />
            No test records in the directory — all {data?.directory_total} organisations are real.
          </p>
        ) : (
          <>
            <p className="text-xs text-slate-500" data-testid="stale-records-summary">
              {candidates.length} of {data.directory_total} rows would go, leaving{' '}
              <strong>{data.remaining_after_purge}</strong> real organisations.
            </p>
            <div className="border rounded-lg divide-y max-h-72 overflow-y-auto"
                 data-testid="stale-records-list">
              {shown.map((c) => (
                <div key={c.id} className="p-2.5 flex items-center justify-between gap-3"
                     data-testid={`stale-record-${c.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-800 truncate">{c.name}</p>
                    <p className="text-[11px] text-slate-400">{c.reason}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize shrink-0">
                    {c.level || 'unspecified'}
                  </Badge>
                </div>
              ))}
            </div>
            {candidates.length > shown.length && (
              <Button variant="ghost" size="sm" className="text-xs"
                      onClick={() => setExpanded(true)} data-testid="stale-records-show-all">
                Show all {candidates.length}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StaleRecordsPanel;
